// Immutable 3-vectors for the OGS subsystems. No three.js import, so node:test can load it.

export type Vector3 = readonly [number, number, number]

const EPSILON = 1e-9

export const ZERO: Vector3 = [0, 0, 0]
export const UP: Vector3 = [0, 1, 0]

export function add(first: Vector3, second: Vector3): Vector3 {
  return [first[0] + second[0], first[1] + second[1], first[2] + second[2]]
}

export function subtract(first: Vector3, second: Vector3): Vector3 {
  return [first[0] - second[0], first[1] - second[1], first[2] - second[2]]
}

export function scale(vector: Vector3, factor: number): Vector3 {
  return [vector[0] * factor, vector[1] * factor, vector[2] * factor]
}

export function dot(first: Vector3, second: Vector3): number {
  return first[0] * second[0] + first[1] * second[1] + first[2] * second[2]
}

export function cross(first: Vector3, second: Vector3): Vector3 {
  return [
    first[1] * second[2] - first[2] * second[1],
    first[2] * second[0] - first[0] * second[2],
    first[0] * second[1] - first[1] * second[0],
  ]
}

export function length(vector: Vector3): number {
  return Math.sqrt(dot(vector, vector))
}

export function distance(first: Vector3, second: Vector3): number {
  return Math.hypot(first[0] - second[0], first[1] - second[1], first[2] - second[2])
}

export function midpoint(first: Vector3, second: Vector3): Vector3 {
  return [
    (first[0] + second[0]) / 2,
    (first[1] + second[1]) / 2,
    (first[2] + second[2]) / 2,
  ]
}

// Throws on zero length: a silent [0,0,0] propagates as a plausible orientation.
export function normalize(vector: Vector3): Vector3 {
  const magnitude = length(vector)
  if (magnitude < EPSILON) throw new Error('vector3.normalize: zero-length vector')
  return [vector[0] / magnitude, vector[1] / magnitude, vector[2] / magnitude]
}

// Unit direction from `from` towards `to`.
export function direction(from: Vector3, to: Vector3): Vector3 {
  return normalize(subtract(to, from))
}

// Angle between two unit vectors, radians. Clamped because a dot of 1+1e-16 is NaN.
export function angleBetweenUnits(first: Vector3, second: Vector3): number {
  return Math.acos(Math.min(1, Math.max(-1, dot(first, second))))
}

// Read vertex `index` out of a packed xyz array.
export function fromArray(values: ArrayLike<number>, index = 0): Vector3 {
  const offset = index * 3
  return [values[offset], values[offset + 1], values[offset + 2]]
}

// A 3-vector out of parsed asset JSON; `what` names the field in the throw.
export function parse(value: unknown, what: string): Vector3 {
  const finite = Array.isArray(value) && value.length === 3
    && value.every((component) => Number.isFinite(component))
  if (!finite) throw new Error(`${what} is not a 3-vector`)
  const components = value as number[]
  return [components[0], components[1], components[2]]
}
